"use client";

// ============================================================================
// useUsageTracker — the browser tracker, with a teardown that actually runs
// ============================================================================
//
// The tracker owns an interval and a `pagehide` listener. A hook that creates
// one and does not dispose it is the first of the four shapes in the 2026-09
// leak sweep (started work with no teardown), and in a client-side-routed app
// it would accumulate one live interval per mount for the life of the tab.
//
// The effect deliberately depends only on values that are stable for the life
// of a page: an inline object literal in the dep array is fresh every render,
// so the effect re-runs, its cleanup disposes the tracker, and the queue is
// flushed on every keystroke that re-renders the parent
// (ASYNC_EFFECT_COMPLETION flags exactly this).
// ============================================================================

import { useEffect, useMemo, useRef } from "react";

import type { UsageCatalog } from "../catalog";
import {
  createUsageTracker,
  type UsageTrackDetail,
  type UsageTracker,
} from "../client/index";

export interface UseUsageTrackerOptions<Name extends string> {
  readonly catalog: UsageCatalog<Name>;
  readonly endpoint: string;
  /** Off by default in tests/SSR; an app passes `false` to disable capture. */
  readonly enabled?: boolean;
  readonly flushIntervalMs?: number;
  readonly onError?: (error: unknown) => void;
}

export interface UseUsageTrackerResult<Name extends string> {
  track(event: Name, detail?: UsageTrackDetail): void;
  flush(): void;
}

export function useUsageTracker<Name extends string>(
  options: UseUsageTrackerOptions<Name>,
): UseUsageTrackerResult<Name> {
  const { catalog, endpoint, enabled = true, flushIntervalMs, onError } = options;
  const trackerRef = useRef<UsageTracker<Name> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const tracker = createUsageTracker<Name>({
      catalog,
      endpoint,
      flushIntervalMs,
      onError,
    });
    trackerRef.current = tracker;
    return () => {
      tracker.dispose();
      trackerRef.current = null;
    };
    // `catalog` is a module-level singleton in every consumer and `onError` is
    // intentionally not a dependency — including it would tie the tracker's
    // lifetime to a callback identity the caller rarely memoizes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, endpoint, flushIntervalMs]);

  return useMemo(
    () => ({
      track(event: Name, detail?: UsageTrackDetail) {
        trackerRef.current?.track(event, detail);
      },
      flush() {
        trackerRef.current?.flush();
      },
    }),
    [],
  );
}
